/**
 * The shrinking safe zone.
 *
 * Schedule, from GAME-DESIGN:
 *
 *   4:00 - 2:00   full arena, no damage
 *   2:00          first contraction, announced 5s early
 *   2:00 - 0:30   contracts toward a seeded centre
 *   0:30 - 0:06   closes to RADIUS ZERO
 *   0:06 - 0:00   nowhere is safe
 *
 * The zero is the whole design. A zone that stops at 35% leaves a pocket where
 * survivors take no damage forever, and in a winner-take-all wager the losing
 * player's best line becomes kiting the clock — a 1.25x Volt can hold that
 * pocket against a 0.85x Monolith indefinitely. Closing fully makes the fight
 * mandatory, and closing six seconds early means it happens on the clock rather
 * than at the buzzer.
 *
 * The centre is drawn from the match seed at world creation and never moves, so
 * the commitment published at lobby lock covers the zone path. Zone geometry
 * decides who the shrink favours; a commitment that omitted it would be
 * decorative.
 */
import { DT, ZONE_CLOSED_TICK, ZONE_DAMAGE_START_TICK, ZONE_DPS_END, ZONE_DPS_MID, ZONE_DPS_START, ZONE_FINAL_START_TICK, ZONE_HIT_EVENT_INTERVAL_TICKS, ZONE_MID_RADIUS_FRACTION, ZONE_WARN_TICKS, } from "./constants.js";
import { dcos, dsin, lerp, ticksToMs, TWO_PI } from "./math.js";
import { nextFloat } from "./rng.js";
import { distXZ } from "./vec.js";
/** The attacker id used for zone damage, so kill attribution can tell it apart. */
export const ZONE_SOURCE_ID = "__zone__";
export function createZone(map, rng) {
    // Two draws, always in this order — the RNG stream is part of the replay.
    const angle = nextFloat(rng) * TWO_PI;
    const dist = Math.sqrt(nextFloat(rng)) * map.zoneCenterJitter;
    return {
        centerX: map.centerX + dsin(angle) * dist,
        centerZ: map.centerZ + dcos(angle) * dist,
        radius: map.startRadius,
        targetRadius: map.startRadius,
        dps: 0,
        startRadius: map.startRadius,
        announcedPhase: 0,
        suppressed: false,
    };
}
export function zoneRadiusAtTick(tick, startRadius) {
    const mid = startRadius * ZONE_MID_RADIUS_FRACTION;
    if (tick < ZONE_DAMAGE_START_TICK)
        return startRadius;
    if (tick < ZONE_FINAL_START_TICK) {
        const u = (tick - ZONE_DAMAGE_START_TICK) / (ZONE_FINAL_START_TICK - ZONE_DAMAGE_START_TICK);
        return lerp(startRadius, mid, u);
    }
    if (tick < ZONE_CLOSED_TICK) {
        const u = (tick - ZONE_FINAL_START_TICK) / (ZONE_CLOSED_TICK - ZONE_FINAL_START_TICK);
        return lerp(mid, 0, u);
    }
    return 0;
}
export function zoneDpsAtTick(tick) {
    if (tick < ZONE_DAMAGE_START_TICK)
        return 0;
    if (tick < ZONE_FINAL_START_TICK) {
        const u = (tick - ZONE_DAMAGE_START_TICK) / (ZONE_FINAL_START_TICK - ZONE_DAMAGE_START_TICK);
        return lerp(ZONE_DPS_START, ZONE_DPS_MID, u);
    }
    if (tick < ZONE_CLOSED_TICK) {
        const u = (tick - ZONE_FINAL_START_TICK) / (ZONE_CLOSED_TICK - ZONE_FINAL_START_TICK);
        return lerp(ZONE_DPS_MID, ZONE_DPS_END, u);
    }
    return ZONE_DPS_END;
}
/** The radius the current or next contraction is heading for. */
function targetRadiusAtTick(tick, startRadius) {
    if (tick < ZONE_FINAL_START_TICK)
        return startRadius * ZONE_MID_RADIUS_FRACTION;
    return 0;
}
/**
 * Advances the zone and applies its damage.
 *
 * Damage lands every tick — a 15dps zone that only ticked twice a second would
 * be dodgeable by frame-timing — but it is only reported on the wire twice a
 * second, because eight players in the storm at 30Hz is 240 events/second of
 * pure noise.
 */
export function stepZone(world, events, onDamage) {
    const zone = world.zone;
    const tick = world.tick;
    // A frozen zone neither contracts nor burns. Checked before the announcement
    // too, so a suppressed zone does not narrate contractions that never happen.
    if (zone.suppressed)
        return;
    announceContractions(world, events);
    zone.radius = zoneRadiusAtTick(tick, zone.startRadius);
    zone.targetRadius = targetRadiusAtTick(tick, zone.startRadius);
    zone.dps = zoneDpsAtTick(tick);
    if (zone.dps <= 0)
        return;
    const report = tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        const d = distXZ(p.x, p.z, zone.centerX, zone.centerZ);
        if (d <= zone.radius)
            continue;
        onDamage(p.id, zone.dps * DT, report);
    }
}
function announceContractions(world, events) {
    const zone = world.zone;
    const tick = world.tick;
    const warnTicks = [
        [1, ZONE_DAMAGE_START_TICK - ZONE_WARN_TICKS, zone.startRadius * ZONE_MID_RADIUS_FRACTION],
        [2, ZONE_FINAL_START_TICK - ZONE_WARN_TICKS, 0],
    ];
    for (const entry of warnTicks) {
        const phase = entry[0];
        const at = entry[1];
        const target = entry[2];
        if (zone.announcedPhase >= phase)
            continue;
        if (tick < at)
            continue;
        zone.announcedPhase = phase;
        events.push({
            t: "zone_shrink",
            tick,
            targetRadius: target,
            warnMs: ticksToMs(ZONE_WARN_TICKS),
        });
    }
}
//# sourceMappingURL=zone.js.map